import { DestroyRef, Injectable, ViewContainerRef } from '@angular/core';
import { HashMap, TranslocoService } from '@jsverse/transloco';
import { map, Observable, of, switchMap, take } from 'rxjs';
import { Router } from '@angular/router';
import { CMSLanguageService } from '@markant-platform/shared/data-access';
import { ApproveComponent } from './approve/approve.component';
import { InfoComponent } from './info/info.component';
import { ErrorDialogComponent } from './error-dialog/error-dialog.component';
import { FurtherQualificationDialogComponent } from './further-qualification-dialog/further-qualification-dialog.component';
import { ConsentDialogComponent } from './consent-dialog/consent-dialog.component';
import { CmsLink, InternalCmsLink } from '@markant-platform/shared/content/model';
import { ContentService } from '@markant-platform/shared/content/data-access';
import { InsignificantChangeNoticeResponseDto } from '@markant-platform/consent/data-access';
import { InitalConsentDialogComponent } from './inital-consent-dialog/inital-consent-dialog.component';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Injectable({
  providedIn: 'root',
})
export class DialogService {
  appComponentViewRef?: ViewContainerRef;
  alreadyTriggered = false;
  timeOutIDs: number[] = [];
  termsOfUseLink$: Observable<InternalCmsLink>;
  privacyPolicyLink$: Observable<InternalCmsLink>;
  serviceGtcLink$: Observable<CmsLink>;

  constructor(
    private readonly translocoService: TranslocoService,
    private readonly cmsLanguageService: CMSLanguageService,
    private readonly contentService: ContentService,
    private readonly router: Router,
    private readonly destroyRef: DestroyRef
  ) {
    this.termsOfUseLink$ = cmsLanguageService.activeCMSLanguage$.pipe(
      switchMap(() => {
        return contentService.getInternalTermsOfUseLink();
      }),
      map(cmsLinks => cmsLinks[0])
    );
    this.privacyPolicyLink$ = cmsLanguageService.activeCMSLanguage$.pipe(
      switchMap(() => {
        return contentService.getInternalPrivacyPolicyLink();
      }),
      map(cmsLinks => cmsLinks[0])
    );
    this.serviceGtcLink$ = cmsLanguageService.activeCMSLanguage$.pipe(
      switchMap(() => {
        return contentService.getServiceGTCsLink(false);
      }),
      map(cmsLinks => cmsLinks[0])
    );
  }

  showApproveDialogWithTransloco(
    pathToHeadline: string,
    pathToText: string,
    approveCallBack: () => void,
    pathToApproveButton?: string,
    pathToCancleButton?: string,
    textOptions?: HashMap<string>,
    closeCallback?: () => void
  ): void {
    if (this.appComponentViewRef) {
      const component = this.appComponentViewRef.createComponent(ApproveComponent);

      this.translocoService
        .selectTranslate(pathToHeadline, textOptions)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(message => (component.instance.headline = message));

      this.translocoService
        .selectTranslate(pathToText, textOptions)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(message => (component.instance.text = message));
      this.translocoService
        .selectTranslate(pathToApproveButton || 'common.approve')
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(message => (component.instance.approveButtonText = message));
      this.translocoService
        .selectTranslate(pathToCancleButton || 'common.cancel')
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(message => (component.instance.cancelButtonText = message));
      component.instance.headlineStyle = { 'text-align': 'left', color: 'var(--mel-markant-red-2)' };
      component.instance.buttonAlign = 'right';
      component.instance.cancelButtonStyles = { colorScheme: 'primary', class: 'mr-4' };
      component.instance.approveButtonStyles = { colorScheme: 'primary' };
      component.instance.closeOnApprove = true;
      component.instance.isApproved.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(isApproved => {
        if (isApproved) {
          approveCallBack();
        }
      });
      component.instance.calledCloseCallback.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(_ => {
        closeCallback?.();
      });
      component.instance.openChange.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(open => {
        if (!open) {
          component.destroy();
        }
      });
      component.instance.open = true;
    }
  }

  showInfoDialogWithTransloco(
    pathToHeadline: string,
    pathToText: string,
    callback?: () => void,
    pathToButtonText?: string,
    showConfirmButton = true
  ): void {
    if (this.appComponentViewRef) {
      const component = this.appComponentViewRef.createComponent(InfoComponent);
      this.translocoService
        .selectTranslate(pathToHeadline)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(message => (component.instance.headline = message));

      this.translocoService
        .selectTranslate(pathToText)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(message => (component.instance.text = message));

      this.translocoService
        .selectTranslate(pathToButtonText || 'common.approve')
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(message => (component.instance.buttonText = message));

      component.instance.openChange.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(open => {
        if (!open) {
          if (callback) {
            callback();
          }
          component.destroy();
        }
      });

      component.instance.open = true;
      component.instance.showConfirmButton = showConfirmButton;
    }
  }

  showErrorDialog(
    headlineTranslationKey: string,
    textTranslationKey: string,
    buttonLabelTranslationKey: string,
    asyncButtonCallback: () => void
  ): void {
    if (!this.appComponentViewRef) {
      return;
    }

    const componentRef = this.appComponentViewRef.createComponent(ErrorDialogComponent);
    const component = componentRef.instance;

    component.headTitle$ = this.translocoService.selectTranslate(headlineTranslationKey);
    component.text$ = this.translocoService.selectTranslate(textTranslationKey);

    component.buttonLabel$ = this.translocoService.selectTranslate(buttonLabelTranslationKey);
    component.buttonCallback = async (): Promise<void> => {
      await asyncButtonCallback();
      componentRef.destroy();
    };

    component.closeCallback = (): void => componentRef.destroy();

    component.open = true;
  }

  showFurtherQualificationDialog(hasOpenFurtherQualificationProcess: boolean | undefined): void {
    if (this.appComponentViewRef && !this.alreadyTriggered) {
      this.alreadyTriggered = true;
      const component = this.appComponentViewRef?.createComponent(FurtherQualificationDialogComponent);
      if (hasOpenFurtherQualificationProcess) {
        component.instance.level2CustomerWithOpenFurtherQualificationProcess = true;
      } else {
        component.instance.level2Customer = true;
      }

      component.instance.shouldClose
        .pipe(take(1), takeUntilDestroyed(this.destroyRef))
        .subscribe(_ => component.destroy());
    }
  }

  clearTimerForFurtherQualificationDialog(id?: number): void {
    if (this.router.url.startsWith('/cartridge/catalog')) {
      if (this.alreadyTriggered) {
        window.clearTimeout(id);
      }

      if (id) {
        this.timeOutIDs.push(id);
      }

      return;
    }

    this.alreadyTriggered = false;

    window.clearTimeout(id);
    this.timeOutIDs.forEach(timeOutId => {
      window.clearTimeout(timeOutId);
    });

    this.timeOutIDs = [];
  }

  showConsentDialog(
    changes: InsignificantChangeNoticeResponseDto[],
    activeLanguage: string
  ): ConsentDialogComponent | null {
    if (!this.appComponentViewRef) {
      return null;
    }
    const componentRef = this.appComponentViewRef.createComponent(ConsentDialogComponent);
    const component = componentRef.instance;
    component.consentChanges = changes;
    component.activeLanguage = activeLanguage;
    component.termsOfUseLink$ = this.termsOfUseLink$;
    component.privacyPolicyLink$ = this.privacyPolicyLink$;
    component.serviceGtcLink$ = this.serviceGtcLink$;
    component.open = true;

    return component;
  }

  showServiceConditionsConsentDialog(
    changes: (InsignificantChangeNoticeResponseDto & {
      url?: string;
    })[],
    activeLanguage: string
  ): ConsentDialogComponent | null {
    if (!this.appComponentViewRef) {
      return null;
    }
    const componentRef = this.appComponentViewRef.createComponent(ConsentDialogComponent);
    const component = componentRef.instance;

    component.consentChanges = changes;
    component.activeLanguage = activeLanguage;
    component.open = true;

    return component;
  }

  showInitialConsentDialog(): Observable<boolean> {
    if (!this.appComponentViewRef) {
      return of(false);
    }
    const componentRef = this.appComponentViewRef.createComponent(InitalConsentDialogComponent);
    const component = componentRef.instance;
    component.termsOfUseLink$ = this.termsOfUseLink$;
    component.privacyPolicyLink$ = this.privacyPolicyLink$;
    component.open = true;
    return component.approveChange;
  }
}
